import React, { useContext, useEffect, useState } from 'react';
import { Menu, Button, Dropdown, Space } from 'antd';
import { toLower } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import ButtonComponent from '@/components/Button';
import { useDispatch } from 'react-redux';
import { logout } from '@/store/auth/authSlice';
import EnglishFlag from '@/assets/images/flags/english.png';
import ThaiLandFlag from '@/assets/images/flags/thailand.png';
import { GlobalContext } from '@/contexts/GlobalContext';
import { parseDateTime } from '@/utils/formatter';
import { ROUTES } from '@/constants';
import { getCurrentLanguage } from '@/utils/locale';
import { ReactComponent as SettingIcon } from '@/assets/images/icons/settings.svg';
import { ReactComponent as LogoutIcon } from '@/assets/images/icons/log-out.svg';
import { ReactComponent as ArrowLeft } from '@/assets/images/icons/left-arrow.svg';
import { ProfileService } from '@/services';
import ModalComponent from '../Modal';

import './index.scss';

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modalConfirmLogoutOpen, setModalConfirmLogoutOpen] = useState(false);
  const {
    pageTitle,
    triggerFetchProfile,
    setTriggerFetchProfile,
    currentUser,
    setCurrentUser,
  } = useContext(GlobalContext);
  const { t, i18n } = useTranslation();
  const lang = getCurrentLanguage();

  const changeLanguage = async (language) => {
    i18n.changeLanguage(language);
    if (language !== lang) {
      const userLanguage = language === 'en' ? 'en_US' : language;
      await ProfileService.updateProfile({ language: userLanguage });
      window.location.reload(false);
    }
  };

  const fetchProfile = async () => {
    const { success, data } = await ProfileService.getProfile();

    if (success) {
      setCurrentUser(data);
    }
  };

  useEffect(() => {
    if (triggerFetchProfile) {
      fetchProfile();
    }

    return () => {
      setTriggerFetchProfile(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerFetchProfile]);

  const acccountMenu = (
    <div className="menu-wrap">
      <div className="merchant-info">MerchantID: {currentUser?.id}</div>
      <Menu
        items={[
          {
            label: t('button.profile'),
            key: 'profile',
            icon: <SettingIcon />,
            onClick: () => navigate('/me'),
          },
          {
            label: t('button.logout'),
            key: 'logout',
            icon: <LogoutIcon />,
            onClick: () => setModalConfirmLogoutOpen(true),
          },
        ]}
      />
    </div>
  );

  const language = (
    <div className="language-container_i18n">
      <Menu
        items={[
          {
            label: (
              <span className={`${lang === 'en' ? 'active' : ''}`}>
                English
              </span>
            ),
            key: 'en',
            icon: <img src={EnglishFlag} alt="icon" />,
            onClick: () => changeLanguage('en'),
          },
          {
            label: (
              <span className={`${lang === 'th' ? 'active' : ''}`}>Thai</span>
            ),
            key: 'th',
            icon: <img src={ThaiLandFlag} alt="icon" />,
            onClick: () => changeLanguage('th'),
          },
        ]}
      />
    </div>
  );

  return (
    <header>
      <div className="header-container">
        <div className="left-content">
          <div className="page-title font-immps-bold">
            {pageTitle?.backUrl && (
              <ButtonComponent
                type="link"
                icon={<ArrowLeft />}
                onClick={() => navigate(pageTitle?.backUrl)}
              />
            )}

            <div className="basic-info">
              {pageTitle?.title && (
                <div className="title">{pageTitle?.title}</div>
              )}
              <div className="subtitle">
                {pageTitle?.createdAt && (
                  <div className="created-at">
                    {parseDateTime(pageTitle?.createdAt)}
                  </div>
                )}
                {pageTitle?.status && (
                  <div className={`sms-tag sms-${toLower(pageTitle?.status)}`}>
                    {t(
                      `common.constants.transaction_status.upper.${pageTitle?.status}`,
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="right-content">
          <div className="topup">
            <div className="sms-number">
              <span>
                {t(
                  'dashboard.summary_section.credit_summary.credit_items.sms_available',
                )}
              </span>
              <span className="number">
                {currentUser?.creditBalance.toLocaleString()}
              </span>
            </div>
            <ButtonComponent
              type="primary"
              onClick={() => navigate(ROUTES.SUBSCRIPTIONS.LIST)}
            >
              {t('button.top_up')}
            </ButtonComponent>
          </div>
          <div className="switch-language" id="sms2buy-switch-language">
            <Dropdown
              placement="bottom"
              overlayClassName="dropdown-custom"
              overlay={language}
              trigger={['click']}
              getPopupContainer={() =>
                document.getElementById('sms2buy-switch-language')
              }
              destroyPopupOnHide
            >
              <Button type="link">
                <img
                  src={lang === 'en' ? EnglishFlag : ThaiLandFlag}
                  alt="icon"
                />
              </Button>
            </Dropdown>
          </div>
          <div className="account-menu" id="sms2buy-account-menu">
            <Dropdown
              overlay={acccountMenu}
              trigger={['click']}
              getPopupContainer={() =>
                document.getElementById('sms2buy-account-menu')
              }
              overlayClassName="dropdown-custom"
              destroyPopupOnHide
            >
              <Button type="link" className="avatar">
                {currentUser?.avatar && (
                  <img
                    src={currentUser?.avatar}
                    alt="icon"
                    className="user-portal__header__icon"
                  />
                )}
                {!currentUser?.avatar && (
                  <div className="avatar__default">
                    <div className="fw-600">
                      {currentUser?.firstName?.slice(0, 1)}
                    </div>
                  </div>
                )}
              </Button>
            </Dropdown>

            <ModalComponent
              centered
              title={t('common.header.logout.title')}
              open={modalConfirmLogoutOpen}
              onOk={() => setModalConfirmLogoutOpen(false)}
              onCancel={() => setModalConfirmLogoutOpen(false)}
              footer={null}
              getContainer={() =>
                document.getElementById('sms2buy-account-menu')
              }
              destroyPopupOnHide
              maskClosable={false}
            >
              <Space>
                <ButtonComponent
                  onClick={() => setModalConfirmLogoutOpen(false)}
                >
                  {t('common.header.logout.btn_no')}
                </ButtonComponent>
                <ButtonComponent
                  type="primary"
                  onClick={() => dispatch(logout())}
                >
                  {t('common.header.logout.btn_yes')}
                </ButtonComponent>
              </Space>
            </ModalComponent>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
